import {
  Activity,
  Bookmark,
  Bot,
  Boxes,
  Building2,
  Calendar,
  ChartLine,
  CirclePlay,
  Database,
  Files,
  type LucideIcon,
  MessageSquare,
  Play,
  Plug,
  Presentation,
  Route,
  Shapes,
  Sparkles,
  User,
  WandSparkles,
  Waypoints,
  Zap,
} from "lucide-react";

/** Every record type with an icon of its own, in {@link ENTITY_ICONS}. */
export const ENTITY_TYPES = [
  "dataset",
  "collection",
  "device",
  "deviceType",
  "session",
  "file",
  "event",
  "topic",
  "action",
  "invocation",
  "agent",
  "agentThread",
  "skill",
  "metric",
  "visualization",
  "view",
  "user",
  "org",
  "integration",
  "trigger",
] as const;

export type EntityType = (typeof ENTITY_TYPES)[number];

/** Human-readable name for a record type, for tooltips and error copy. */
export const ENTITY_TYPE_LABELS: Readonly<Record<EntityType, string>> = {
  action: "Action",
  agent: "Agent",
  agentThread: "Agent thread",
  collection: "Collection",
  dataset: "Dataset",
  device: "Device",
  deviceType: "Device type",
  event: "Event",
  file: "File",
  integration: "Integration",
  invocation: "Invocation",
  metric: "Metric",
  org: "Organization",
  session: "Session",
  skill: "Skill",
  topic: "Topic",
  trigger: "Trigger",
  user: "User",
  view: "View",
  visualization: "Visualization",
};

/**
 * The one icon for each record type, wherever the type is shown. Lucide only.
 * Changing a type's icon is a one-line edit here. Render through
 * `EntityIcon` rather than importing these glyphs elsewhere; a lint rule
 * holds that line. Specific integrations keep their brand logos
 * (`IntegrationIcon`); `integration` is the generic one.
 */
export const ENTITY_ICONS: Readonly<Record<EntityType, LucideIcon>> = {
  dataset: Database,
  collection: Shapes,
  device: Bot,
  deviceType: Boxes,
  session: Route,
  file: Files,
  event: Calendar,
  topic: Activity,
  action: Zap,
  invocation: CirclePlay,
  agent: Sparkles,
  agentThread: MessageSquare,
  skill: WandSparkles,
  metric: ChartLine,
  visualization: Presentation,
  view: Bookmark,
  user: User,
  org: Building2,
  integration: Plug,
  trigger: Waypoints,
};

/**
 * Icons for verbs, kept apart from the record types they act on: running an
 * action is `Play`, while the Action itself is `ENTITY_ICONS.action`.
 */
export const ACTION_VERB_ICONS = {
  run: Play,
} as const satisfies Record<string, LucideIcon>;
